import {
  Box,
  Button,
  Flex,
  Heading,
  List,
  ListItem,
  Text,
  useColorMode,
} from "@chakra-ui/react";
import { SmallCloseIcon } from "@chakra-ui/icons";

import { getCurrentColor } from "../../constants";
import { useLocation } from "react-router-dom";
import useFilter from "../../hooks/useFilter";
import useSelectedFilter from "../../hooks/useSelectedFilter";
import { transformFilterName } from "../../utils/transformFilterName";

const SelectedFilters = () => {
  const location = useLocation();
  const { appliedFilters, ClearFilter } = useFilter();
  const { deleteParam } = useSelectedFilter();
  const { colorMode } = useColorMode();

  const filterEntries = appliedFilters
    ? Object.entries(appliedFilters).filter(
        ([key]) => key !== "sf" && key !== "condition",
      )
    : [];

  const hasActiveFilters = filterEntries.some(([key, values]) => {
    if (Array.isArray(values)) {
      return values.length > 0;
    }
    // For string values (keyword filters)
    return typeof values === "string" && values.trim().length > 0;
  });

  return hasActiveFilters ? (
    <Box
      borderRadius={"6px"}
      border={"1px solid"}
      borderColor={getCurrentColor(colorMode, "grayC.500", "transparent")}
      mb={"22px"}
    >
      <Heading
        as={"h3"}
        fontSize={"lg"}
        color={getCurrentColor(colorMode, "blackC.200", "grayC.1600")}
        fontFamily={"mainFont"}
        fontWeight={"500"}
        w={"100%"}
        bg={getCurrentColor(colorMode, "grayC.600", "grayC.1700")}
        p={"18px"}
        borderRadius={"6px 6px 0px 0px"}
      >
        Current Filters
      </Heading>
      <Flex
        p={"10px"}
        bg={getCurrentColor(colorMode, "whiteC.500", "grayC.1900")}
        flexDirection={"column"}
        borderTop={"1px solid"}
        borderColor={getCurrentColor(colorMode, "grayC.500", "grayC.1900")}
        borderRadius={"0px 0px 6px 6px"}
      >
        {filterEntries.map(([key, values]) => {
          // Skip if no values
          if (Array.isArray(values) && values.length === 0) return null;
          if (typeof values === "string" && values.trim().length === 0)
            return null;

          // Handle keyword filters (string values)
          if (
            typeof values === "string" &&
            (key === "s_in" || key === "s_out")
          ) {
            const label =
              key === "s_in" ? "Include Keywords" : "Exclude Keywords";
            const keywords = values
              .split(",")
              .map((k) => k.trim())
              .filter((k) => k.length > 0);

            return (
              <Flex
                key={key}
                fontSize={"sm"}
                fontWeight={"600"}
                pr={3}
                position={"relative"}
                gap={"5px"}
                flexDirection={"column"}
              >
                <Text fontSize={"md"} fontWeight={"600"}>
                  {label}:
                </Text>
                <List pl={2}>
                  {keywords.map((keyword) => (
                    <ListItem
                      key={`${key}-${keyword}`}
                      display={"flex"}
                      justifyContent={"space-between"}
                      alignItems={"center"}
                    >
                      <Flex
                        gap={"3px"}
                        fontSize={"sm"}
                        fontWeight={"400"}
                        display={"inline"}
                        flexWrap={"wrap"}
                        wordBreak={"break-word"}
                      >
                        - {keyword}
                      </Flex>
                      <SmallCloseIcon
                        onClick={() => {
                          const remainingKeywords = keywords.filter(
                            (k) => k !== keyword,
                          );
                          deleteParam(key, keyword);
                        }}
                        cursor={"pointer"}
                        right={0}
                        top={"4px"}
                        color={getCurrentColor(
                          colorMode,
                          "blackC.200",
                          "grayC.1600",
                        )}
                      />
                    </ListItem>
                  ))}
                </List>
              </Flex>
            );
          }

          // Handle array filters (existing logic)
          if (!Array.isArray(values)) return null;

          const data = transformFilterName(location.pathname, key, {
            title: key,
            value: values.map((item) => item.value),
            flag: values.map((item) => item.flag || item.value),
          });

          return (
            <Flex
              key={key + toString(values)}
              fontSize={"sm"}
              fontWeight={"600"}
              pr={3}
              position={"relative"}
              textTransform={"capitalize"}
              gap={"5px"}
              flexDirection={"column"}
            >
              <Text fontSize={"md"} fontWeight={"600"}>
                {data.title}:
              </Text>
              <List pl={2}>
                {data.value.map((item, i) => (
                  <ListItem
                    key={item}
                    display={"flex"}
                    justifyContent={"space-between"}
                    alignItems={"center"}
                  >
                    <Flex
                      gap={"3px"}
                      fontSize={"sm"}
                      fontWeight={"400"}
                      display={"inline"}
                      flexWrap={"wrap"}
                      textTransform={"capitalize"}
                      wordBreak={"break-word"}
                    >
                      - {item}
                    </Flex>
                    <SmallCloseIcon
                      onClick={() => {
                        deleteParam(data.query, data.flag[i] || item);
                      }}
                      cursor={"pointer"}
                      right={0}
                      top={"4px"}
                      color={getCurrentColor(
                        colorMode,
                        "blackC.200",
                        "grayC.1600",
                      )}
                    />
                  </ListItem>
                ))}
              </List>
            </Flex>
          );
        })}
        <Button
          p={0}
          display={"flex"}
          mt={"17px"}
          justifyContent={"flex-start"}
          variant={"underline"}
          onClick={() => ClearFilter()}
          color={getCurrentColor(colorMode, "blackC.200", "grayC.1600")}
          textDecoration={"underline"}
          _hover={{ textDecoration: "none" }}
        >
          Clear All
        </Button>
      </Flex>
    </Box>
  ) : null;
};

export default SelectedFilters;
